import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyGetLessonActionQuery } from "../../services/users/ai";
import { streamPost } from "../../utils/aiSse";
import { toastAiError } from "../../utils/aiErrors";

const ACTION_TYPES = [
  "explain_simple",
  "key_takeaways",
  "real_life_example",
  "summarize",
  "translate_arabic",
];

const QuickActionsPanel = ({ lessonId }) => {
  const { t } = useTranslation("ai");
  const [getAction] = useLazyGetLessonActionQuery();
  const [activeType, setActiveType] = useState(null);
  const [contentByType, setContentByType] = useState({});
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [incomplete, setIncomplete] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    setActiveType(null);
    setContentByType({});
    setStreamText("");
    setStreaming(false);
    setIncomplete(false);
    abortRef.current?.abort();

    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(
        ACTION_TYPES.map(async (type) => {
          const result = await getAction({ lessonId, type });
          if (!result.error && result.data?.content) {
            next[type] = result.data.content;
          }
        }),
      );
      if (!cancelled) setContentByType(next);
    })();

    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
  }, [lessonId, getAction]);

  const runAction = useCallback(
    async (actionType) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setActiveType(actionType);
      setIncomplete(false);

      if (contentByType[actionType]) {
        setStreamText(contentByType[actionType]);
        setStreaming(false);
        return;
      }

      setStreaming(true);
      setStreamText("");

      try {
        let assembled = "";
        const result = await streamPost(
          `/api/v1/user/ai/lessons/${lessonId}/actions`,
          { action_type: actionType },
          {
            signal: controller.signal,
            onToken: (token) => {
              assembled += token;
              setStreamText(assembled);
            },
          },
        );

        if (result.json?.cached && result.json?.content) {
          assembled = result.json.content;
          setStreamText(assembled);
        }

        setContentByType((prev) => ({ ...prev, [actionType]: assembled }));
        setStreaming(false);
      } catch (err) {
        if (controller.signal.aborted) return;
        setStreaming(false);
        if (err.incomplete) {
          setIncomplete(true);
          return;
        }
        toastAiError(err, t);
      }
    },
    [contentByType, lessonId, t],
  );

  const displayed = streaming
    ? streamText
    : (activeType && contentByType[activeType]) || streamText || "";

  return (
    <div className="ai-quick-actions">
      <div className="ai-quick-actions__buttons">
        {ACTION_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            className={`btn font-15 ${
              activeType === type ? "btn-main-color" : "btn-main-color-border"
            }`}
            onClick={() => runAction(type)}
            disabled={streaming && activeType === type}
          >
            {t(`actions.${type}`)}
          </button>
        ))}
      </div>

      {(streaming || displayed || incomplete) && (
        <div className="ai-result-card">
          {streaming && !displayed && (
            <p className="ai-muted">{t("actions.generating")}</p>
          )}
          {displayed && <pre className="ai-pre">{displayed}</pre>}
          {incomplete && (
            <div className="ai-retry-row">
              <p className="ai-muted mb-1">{t("actions.incomplete")}</p>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => activeType && runAction(activeType)}
              >
                {t("actions.retry")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuickActionsPanel;
